<template>
  <div class="overflow-y-auto tabs-top-separator" style="height: calc(100% - 58px)" infinite-wrapper data-qa="alarm-table-wrapper">
    <v-skeleton-loader :loading="alarmsSkeleton" type="table">
      <corvina-table
        class="custom-corvina-table alarm-table"
        id="alarm-table"
        :headers="alarmHeaders"
        :items="modelValue.content"
        :itemsPerPage="modelValue.size"
        :currentPage="modelValue.number"
        :itemsLength="modelValue.totalElements"
        :loadingBar="alarmsLoading"
        @currentPageChanged="currentPageChanged"
        @currentItemsPerPageChanged="currentItemsPerPageChanged"
        :isExpandable="false">
      </corvina-table>
    </v-skeleton-loader>
  </div>
</template>

<script setup lang="ts">
import { markRaw, onMounted, ref, watch } from "vue";
import { debounce } from "lodash";
import { CORVINA } from "@corvina/vue-components-library";
import { useI18n } from "vue-i18n";

import { dataStore } from "../stores/data";
import { ALARM_TYPES_DB, AlarmOrderBy, SortDTO } from "../utils/type.util";
import { IAlarm, IAlarmFilter, IAlarmsProp } from "../interfaces/IAlarm";
import { emptyPage, IPage } from "../interfaces/IPage";
import AlarmsTableActions from "./AlarmsTableActions.vue";


const { modelValue = {
  ...emptyPage,
  filters: {
    orderBy: AlarmOrderBy.name,
    orderDir: SortDTO.ASC,
  } as IAlarmFilter,
} } = defineProps<{ modelValue?: IAlarmsProp; }>();

const alarmsSkeleton = ref(true);
const alarmsLoading = ref(true);

const emit = defineEmits<{
  (e: 'update:modelValue', value: IAlarmsProp): void;
}>();

let debouncedRefresh = null as any;

const modelPatch: Partial<IAlarmsProp> = {};
  const debouncedUpdate = debounce(() => {
  emit('update:modelValue', { ...modelValue, ...modelPatch });
  Object.keys(modelPatch).forEach((key) => delete modelPatch[key]);
}, 100);
const patchModel = (patch: Partial<IAlarmsProp>) => {
  Object.assign(modelPatch, patch);
  debouncedUpdate();
};

const { t, d, locale } = useI18n();

const dataStoreInstance = dataStore();

const alarmHeaders = ref([
  {
    key: "enabled",
    title: t("table.enabled"),
    tooltip: t("table.enabled"),
    alignHeaderTooltip: "bottom",
    align: "center",
    id: "enabled",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.ICON,
    propIcon: "enabledIcon",
  },
  {
    key: "name",
    title: t("table.name"),
    tooltip: t("table.name"),
    alignHeaderTooltip: "bottom",
    align: "left",
    id: "name",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "name",
  },
  {
    key: "description",
    title: t("table.description"),
    tooltip: t("table.description"),
    alignHeaderTooltip: "bottom",
    align: "left",
    id: "description",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "description",
  },
  {
    key: "type",
    title: t("table.type"),
    tooltip: t("table.type"),
    alignHeaderTooltip: "bottom",
    align: "left",
    with: "20vw",
    id: "type",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "type",
  },
  {
    key: "groups",
    title: t("table.groups"),
    tooltip: t("table.groups"),
    alignHeaderTooltip: "bottom",
    align: "left",
    with: "20vw",
    id: "groups",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "groups",
  },
  {
    key: "severity",
    title: t("table.shortSeverity"),
    tooltip: t("table.severity"),
    alignHeaderTooltip: "bottom",
    align: "left",
    id: "severity",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.ICON,
    propIcon: "severityIcon",
  },
  {
    key: "updatedAt",
    title: t("table.updatedAt"),
    tooltip: t("table.updatedAt"),
    alignHeaderTooltip: "bottom",
    align: "left",
    id: "updatedAt",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.STRING,
    propString: "updatedAt",
  },
  {
    key: "actions",
    title: t("table.short.actions"),
    tooltip: t("table.availableActions"),
    alignHeaderTooltip: "bottom",
    align: "center",
    id: "actions",
    sortable: false,
    type: CORVINA.TABLE.CELL_TYPE.VUE_COMPONENT,
    propVueComponent: "actionsComponent",
    propCellClass: "actionCellClass",
  },
]);

const currentPageChanged = async (newVal: number) => {
  patchModel({ number: newVal, last: newVal == modelValue.totalPages });
};

const currentItemsPerPageChanged = async (newVal: number) => {
  patchModel({ size: newVal, number: 1 });
};

watch(() => modelValue.number, async (newVal: number) => {
  await refresh();
});

watch(() => modelValue.size, async (newVal: number) => {
  await refresh();
});

const fetchAlarms = async () => {
  const alarms = modelValue;

  setTimeout(() => {
    if (alarmsLoading.value) alarmsSkeleton.value = true;
  }, 300);

  await dataStoreInstance.fetchAlarms({
    filters: alarms.filters,
    page: (alarms.number || 1) - 1,
    pageSize: alarms.size,
  });

  alarmsLoading.value = false;
  alarmsSkeleton.value = false;
};

const refresh = async () => {
  if (debouncedRefresh == null)
    debouncedRefresh = debounce(fetchAlarms, 200);
  await debouncedRefresh();
};

const format = (value) => {
  if (value != "" && value != null) {
    return d(new Date(value), 'dateHour', locale.value);
  }
};

const severityToColor = (value) => {
  const val = parseInt(value);
  if (val < 1) return "good";
  if (val < 3) return "warning-mild";
  if (val < 5) return "warning-serious";
  if (val < 7) return "alert";
}

const severityToExclamation = (value) => {
  if (parseInt(value) % 2 == 0) {
    return "Hight";
  } else {
    return "Low";
  }
}

watch( () => { return dataStoreInstance.getAlarms }, (newPage: IPage<IAlarm>) => {
  const emittedValue = {
    ...modelValue,
    totalElements: dataStoreInstance.getAlarms.totalElements,
    totalPages: dataStoreInstance.getAlarms.totalPages,
    // rewrite content
    content: dataStoreInstance.getAlarms.content.map((alarm) => {
      const deviceGroupsEvents = alarm.events.find(
        (e) => e.label === "CONDITION ON"
      )?.deviceGroups;
      const deviceGroups = dataStoreInstance.getDeviceGroups.filter((dg) =>
        deviceGroupsEvents?.includes(dg.name)
      ).map(dg => dg.label).join(', ');

      return {
        ...alarm,
        type: ALARM_TYPES_DB.filter(at => at.id == alarm.type).length > 0 ? ALARM_TYPES_DB.filter(at => at.id == alarm.type)[0].alias : alarm.type,
        updatedAt: format(alarm.updatedAt),
        enabledIcon: [
          { icon: alarm.enabled ? 'ecc:ecc-E-DeActive' : 'ecc:ecc-E-Copy-copia-2', tooltip: t("table.enabled") + ": " + alarm.enabled }
        ],
        severityIcon: [
          { icon: 'ecc:ecc-ALLARM-'+severityToExclamation(alarm.severity), tooltip: t("table.severity") + ': ' + alarm.severity, color: severityToColor(alarm.severity) }
        ],
        groups: deviceGroups,
        actionsComponent: {
          component: markRaw(AlarmsTableActions),
          props: {
            alarm,
            onRefresh: (_) => {
              refresh();
            },
          },
        },
      };
    }),
  };
  emit("update:modelValue", emittedValue);
});

defineExpose({
  refresh,
});

onMounted(async () => {
  refresh();
});
</script>

<style>
.v-data-table-column {
  height: 40px !important;
}
.custom-corvina-table {
  width: 100%;
}
</style>